export const useMembersArea = () => {
  const route = useRoute()
  const event = useRequestEvent()
  const currentSite = inject("currentSite")
  const userStore = useUserStore()
  const { $api } = useNuxtApp()

  const path = computed(() => {
    if (import.meta.server && event) {
      return event.node.req.url || ""
    }
    return route.path
  })

  const inMembersArea = computed(() => {
    return path.value.startsWith("/members")
  })

  const inTourArea = computed(() => {
    return !inMembersArea.value
  })

  const isGuest = computed(() => {
    return false // ???
  })

  const streamingOnly = computed(() => {
    if (currentSite.value.cachedSlug === "pornplus") {
      return userStore.accessLevel === "streaming"
    } else {
      return false
    }
  })

  const hasPlatinum = computed(() => {
    switch (currentSite.value.installation) {
      case "bp":
        return true
      default:
        return userStore.accessLevel === "lifetime"
    }
  })

  const nextUpgradeAvailableType = computed(() => {
    const lifetimeOfferWindow = new Date()
    lifetimeOfferWindow.setDate(lifetimeOfferWindow.getDate() - 7)
    const joinedAt = new Date(userStore.joinedAt)

    if (userStore.expired) {
      return "reactivate"
    } else if (userStore.trial) {
      return "trial"
    } else if (isGuest.value) {
      return "guest"
    } else if (!hasPlatinum.value && joinedAt >= lifetimeOfferWindow) {
      return "lifetime"
    }
  })

  // NOTE: Used to show either live cam ads or external offers
  // in the members area.
  const camsDisplayRate = 0.33
  const showCamAds = computed(() => {
    if (currentSite.value.gender === "guys") {
      // NOTE: We only show live cam ads on the gay sites.
      return true
    } else {
      return Math.random() < camsDisplayRate
    }
  })

  // NOTE: Only show external offers to full active members.
  // Otherwise, get the user to focus on their relevant upgrade.
  const showExternalOffers = computed(() => {
    return !["trial", "expired", "reactivate", "guest"].includes(
      userStore.accessLevel,
    )
  })

  // This nested structure means:
  // Outer objects are the "from" layer
  // Inner objects are the "to" layer
  // I.e. From trial to monthly:
  // trial_upgrade: {
  //   monthly: {
  const billerCapabilities = {
    none: {
      lifetime: {
        lifetime: {
          enabled: true,
          original_price: 999.99,
          price: 666.66,
          percentage_off: 33,
        },
        monthly: {
          enabled: true,
          original_price: 99.99,
          price: 66.66,
          percentage_off: 33,
        },
      },
      downloads: {
        downloads: {
          enabled: true,
          original_price: 39.95,
          price: 39.95,
          percentage_off: 0,
        },
      },
      trial_upgrade: {
        monthly: {
          enabled: true,
          original_price: 99.99,
          price: 66.66,
          percentage_off: 33,
        },
        quarterly: {
          enabled: true,
          original_price: 999.99,
          price: 666.66,
          percentage_off: 33,
        },
      },
      reactivate: {
        monthly: {
          enabled: true,
          original_price: 99.99,
          price: 66.66,
          percentage_off: 33,
        },
      },
      guest: {
        monthly: {
          enabled: true,
          original_price: 99.99,
          price: 66.66,
          percentage_off: 33,
        },
      },
    },

    epoch: {
      lifetime: {
        lifetime: {
          enabled: true,
          original_price: 149.95,
          price: 119.95,
          percentage_off: 25,
        },
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 24.95,
          percentage_off: 38,
        },
      },
      downloads: {
        downloads: {
          enabled: true,
          original_price: 39.95,
          price: 39.95,
          percentage_off: 0,
        },
      },
      trial_upgrade: {
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 24.95,
          percentage_off: 38,
        },
        quarterly: {
          enabled: true,
          original_price: 59.95,
          price: 39.95,
          percentage_off: 33,
        },
      },
      reactivate: {
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 17.95,
          percentage_off: 55,
        },
      },
      guest: {
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 14.95,
          percentage_off: 62,
        },
      },
    },

    segpay: {
      lifetime: {
        lifetime: {
          enabled: true,
          original_price: 149.95,
          price: 119.95,
          percentage_off: 25,
        },
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 24.95,
          percentage_off: 38,
        },
      },
      downloads: {
        downloads: {
          enabled: true,
          original_price: 39.95,
          price: 39.95,
          percentage_off: 0,
        },
      },
      trial_upgrade: {
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 24.95,
          percentage_off: 38,
        },
        quarterly: {
          enabled: true,
          original_price: 59.95,
          price: 39.95,
          percentage_off: 33,
        },
      },
      reactivate: {
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 17.95,
          percentage_off: 55,
        },
      },
      guest: {
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 14.95,
          percentage_off: 62,
        },
      },
    },

    vendo: {
      lifetime: {
        lifetime: {
          enabled: true,
          original_price: 149.95,
          price: 119.95,
          percentage_off: 25,
        },
      },
      trial_upgrade: {
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 24.95,
          percentage_off: 0,
        },
        yearly: {
          enabled: false,
          original_price: 149.95,
          price: 119.4,
          percentage_off: 0,
        },
      },
      reactivate: {
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 17.95,
          percentage_off: 55,
        },
      },
      guest: {
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 14.95,
          percentage_off: 62,
        },
      },
    },

    webbilling: {
      lifetime: { enabled: false },
      trial_upgrade: {
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 24.95,
          percentage_off: 0,
        },
        yearly: {
          enabled: false,
          original_price: 149.95,
          price: 119.4,
          percentage_off: 0,
        },
      },
      reactivate: {
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 17.95,
          percentage_off: 55,
        },
      },
      guest: {
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 14.95,
          percentage_off: 62,
        },
      },
    },

    wts: {
      lifetime: { enabled: false },
      trial_upgrade: {
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 24.95,
          percentage_off: 0,
        },
        yearly: {
          enabled: false,
          original_price: 149.95,
          price: 119.4,
          percentage_off: 0,
        },
      },
      reactivate: {
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 17.95,
          percentage_off: 55,
        },
      },
      guest: {
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 14.95,
          percentage_off: 62,
        },
      },
    },

    rocketgate: {
      lifetime: {
        lifetime: {
          enabled: true,
          original_price: 149.95,
          price: 119.95,
          percentage_off: 25,
        },
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 24.95,
          percentage_off: 38,
        },
      },
      downloads: {
        downloads: {
          enabled: true,
          original_price: 39.95,
          price: 39.95,
          percentage_off: 0,
        },
      },
      trial_upgrade: {
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 24.95,
          percentage_off: 38,
        },
        quarterly: {
          enabled: true,
          original_price: 59.95,
          price: 39.95,
          percentage_off: 33,
        },
      },
      reactivate: {
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 17.95,
          percentage_off: 55,
        },
      },
      guest: {
        monthly: {
          enabled: true,
          original_price: 39.95,
          price: 14.95,
          percentage_off: 62,
        },
      },
    },
  }

  // Site overrides for biller capabilities.
  // This is used to override the default
  // biller capabilities for specific sites.
  const billerCapabilitiesSiteOverrides = {
    trial_upgrade: {
      wetvr: {
        monthly: { original_price: 39.95, price: 14.95, percentage_off: 38 },
      },
      pornplus: {
        monthly: { original_price: 39.95, price: 14.95, percentage_off: 38 },
      },
    },

    lifetime: {
      pornplus: {
        lifetime: {
          enabled: true,
          original_price: 239.95,
          price: 189.95,
          percentage_off: 25,
        },
        monthly: { original_price: 39.95, price: 14.95, percentage_off: 38 },
      },
      strippers4k: {
        lifetime: {
          enabled: true,
          original_price: 239.95,
          price: 189.95,
          percentage_off: 25,
        },
      },
    },
    downloads: {
      pornplus: {
        downloads: {
          enabled: true,
          original_price: 39.95,
          price: 39.95,
          percentage_off: 0,
        },
      },
    },
  }

  const normalizedBiller = /segpay/i.test(userStore.biller)
    ? "segpay"
    : userStore.biller?.toLowerCase()

  const fetchBillerCapabilitySiteOverride = (feature, option, key) => {
    return (
      billerCapabilitiesSiteOverrides[feature]?.[currentSite.value.slug]?.[
        option
      ]?.[key] || false
    )
  }

  const fetchBillerCapability = (feature, option, key) => {
    if (fetchBillerCapabilitySiteOverride(feature, option, key)) {
      return fetchBillerCapabilitySiteOverride(feature, option, key)
    }

    return (
      billerCapabilities[normalizedBiller]?.[feature]?.[option]?.[key] || false
    )
  }

  const billerOptionEnabled = (feature, option) => {
    return fetchBillerCapability(feature, option, "enabled")
  }

  const billerOptionOriginalPrice = (feature, option) => {
    return fetchBillerCapability(feature, option, "original_price")
  }

  const billerOptionPrice = (feature, option) => {
    return fetchBillerCapability(feature, option, "price")
  }

  const billerOptionPercentageOff = (feature, option) => {
    return fetchBillerCapability(feature, option, "percentage_off")
  }

  const billerOptionSavings = (feature, option) => {
    const percentageOff = billerOptionPercentageOff(feature, option)
    return percentageOff > 0
  }

  const downloadsUpgradeCost = computed(() => {
    return fetchBillerCapability("downloads", "downloads", "price")
  })

  const upgradeResult = ref(null)
  const upgradeError = ref("")
  const performingMembershipUpgrade = ref(false)
  const performMembershipUpgrade = async (type, { offerId = null } = {}) => {
    upgradeResult.value = false
    upgradeError.value = ""
    performingMembershipUpgrade.value = true

    // HACK: Where is this value coming from?
    if (type === "platinum") {
      type = "lifetime"
    }

    try {
      const response = await $api(`/api/upgrade/${type}`, {
        method: "POST",
        body: JSON.stringify({ option: offerId ? offerId : type }),
      })

      if (response.result === "success") {
        upgradeResult.value = true
      } else if (response.redirect && !response.redirect.match(/\/members/)) {
        // /members check is a holdover from the old app.
        // Now we can refresh the member inline.
        window.location.href = response.redirect
      } else if (response.error) {
        upgradeResult.value = false
        upgradeError.value = response.error
      }
    } finally {
      performingMembershipUpgrade.value = false
    }
  }

  const { isMobile } = useResponsive()
  const continueToSiteAsGuest = async (site, releaseId) => {
    const siteId = (site.slug || site.cachedSlug).replace("-", "")
    const params = new URLSearchParams({ sid: siteId })

    if (releaseId) {
      params.append("vid", releaseId.toString())
    }

    const url = `/api/members/grant_guest_access?${params.toString()}`
    const response = await $api(url)

    // NOTE: On iOS you can't open a new tab.
    if (isMobile.value) {
      window.location.href = response.url
    } else {
      const popup = window.open(response.url, "_blank")

      //Safari blocks popups. Page will navigate if the popup is blocked.
      if (!popup || popup.closed || typeof popup.closed === "undefined") {
        window.location.href = response.url
      }
    }
    return
  }

  // TODO: This is horrible. We are getting "dupes" from the backend. mvft and myveryfirsttime
  // We need to see if we can fix that there, or have to keep this.
  const duplicatedSites = [
    "bhouse",
    "byd",
    "damn",
    "gcr",
    "gvs",
    "msb",
    "mvft",
    "ock",
    "ohm",
    "pm",
    "tnb",
  ]

  // More hacky. These sites shouldn't be in this list.
  const excludedSites = [
    "18yo_com",
    "amv",
    "cmd",
    "cockcomp",
    "crp",
    "cso",
    "csp",
    "d18",
    "deepthroat",
    "eurohump",
    "flex",
    "fob",
    "foc",
    "hmltd",
    "fourty",
    "jck",
    "lolc",
    "massagecreep",
    "mfh",
    "pornpros",
    "pornprosn",
    "pmp",
    "psr",
    "pbv",
    "realexgf",
    "sleep",
    "sqd",
    "tbf",
    "tdg",
    "wch",
  ]

  const { hasExclusiveSeries, shortNameToLongNameMapping } = useSiteSettings(
    currentSite.value,
  )
  const networkSiteLabels = userStore.siteAccessList
    .map((site) => ({
      name: site,
      cachedSlug: site,
    }))
    .filter((site) => !duplicatedSites.includes(site.name.toLowerCase()))
    .filter((site) => !excludedSites.includes(site.name.toLowerCase()))

  networkSiteLabels.unshift({
    base: true,
    name: userStore.siteName,
    cachedSlug: shortNameToLongNameMapping(userStore.siteShortName),
  })

  return {
    continueToSiteAsGuest,
    billerOptionEnabled,
    billerOptionOriginalPrice,
    billerOptionPrice,
    billerOptionPercentageOff,
    billerOptionSavings,
    downloadsUpgradeCost,
    inMembersArea,
    inTourArea,
    networkSiteLabels,
    nextUpgradeAvailableType,
    performMembershipUpgrade,
    performingMembershipUpgrade,
    showCamAds,
    showExternalOffers,
    streamingOnly,
    upgradeError,
    upgradeResult,
  }
}
